import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getBasicFilteringOptions,
  getClearBasicModel,
} from "../../common/EntitiesManagement";
import AddOrEditBasicEntity from "../../components/common/AddOrEditBasicEntity";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import WarehouseDetail from "./WarehouseDetail";
import EntityPrinter from "../../components/common/EntityPrinter";
import PrinterModal from "../../components/common/PrinterModal";

export default function Warehouse() {
  const {
    warehouseModel,
    setWarehouseModel,
    warehouseData,
    WAREHOUSE_ENDPOINT,
    config,
    pageIndexWarehouse,
    setPageIndexWarehouse,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();

  const { id } = useParams();
  const [warehouseDataFiltered, setWarehouseDataFiltered] = useState(
    warehouseData.Data
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPrinterModal, setShowPrinterModal] = useState(false);
  const [showWarehousePrinterModal, setShowWarehousePrinterModal] =
    useState(false);
  const [warehouseSuppliesForPrinting, SetWarehouseSuppliesForPrinting] =
    useState([]);

  useEffect(() => {
    setBasicFilterOptions(getBasicFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const operationalDataObj = warehouseData.Data.find(
        (operationalAreaObj) => operationalAreaObj.Id === Number(id)
      );
      setWarehouseModel(operationalDataObj);
    }
  }, [id]);

  useEffect(() => {
    setWarehouseDataFiltered(warehouseData.Data);
  }, [warehouseData]);

  const validateOperationResult = (result, isDelete) => {
    setWarehouseModel(getClearBasicModel());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([WAREHOUSE_ENDPOINT, config]);
    if (isDelete) {
      if (pageIndexWarehouse !== 0 && warehouseModel.Data.length === 1) {
        setPageIndexWarehouse(pageIndexWarehouse - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${WAREHOUSE_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${WAREHOUSE_ENDPOINT}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${WAREHOUSE_ENDPOINT}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !warehouseModel?.Name ||
    warehouseModel?.Name.replaceAll(" ", "").length <= 0;

  const getColumnTitles = () => {
    return [
      localize.name,
      localize.description,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  const printWarehouse = () => {
    setShowWarehousePrinterModal(true);
    setShowEditModal(false);
  };

  const getWarehouseSuppliesColumns = () => [
    localize.code,
    localize.supply,
    localize.quantity,
  ];

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showWarehousePrinterModal && (
        <PrinterModal
          show={showWarehousePrinterModal}
          setShow={setShowWarehousePrinterModal}
          modalTitle={localize.print}
          reportTitle={localize.warehouse}
          reportSubtitle={warehouseModel.Name}
          columnHeaders={getWarehouseSuppliesColumns()}
          data={warehouseSuppliesForPrinting}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.warehouses}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={WAREHOUSE_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="warehouse"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_warehouse}
          onClickToSubmit={() => {
            handleUpdateRequest(warehouseModel);
            setShowEditModal(false);
            setWarehouseModel(getClearBasicModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setWarehouseModel(getClearBasicModel());
          }}
          Component={
            <WarehouseDetail
              model={warehouseModel}
              setModel={setWarehouseModel}
              setSuppliesForPrinting={SetWarehouseSuppliesForPrinting}
            />
          }
          validators={disableSaveChanges()}
          customSecondaryAction={{
            name: localize.print,
            action: () => {
              printWarehouse();
            },
          }}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.warehouses}
            subtitle={localize.warehouse_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => setShowPrinterModal(true)}
            entityName="warehouse_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_warehouse}
                onClickToSubmit={() => {
                  handleCreateRequest(warehouseModel);
                  setShowAddModal(false);
                  setWarehouseModel(getClearBasicModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setWarehouseModel(getClearBasicModel());
                }}
                Component={
                  <AddOrEditBasicEntity
                    customWidth="100%"
                    nameMandatory
                    model={warehouseModel}
                    setModel={setWarehouseModel}
                  />
                }
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      <DeleteConfirmationMessage
        entityName={warehouseModel.Name}
        entityType={localize.warehouse}
        onClickForCancel={() => {
          toggleVisibleModalConfirm();
          setWarehouseModel(getClearBasicModel());
        }}
        onClickForConfirm={() => {
          handleDeleteRequest(warehouseModel.Id);
          setWarehouseModel(getClearBasicModel());
        }}
      />

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexWarehouse}
            setPageIndex={setPageIndexWarehouse}
            dataArray={warehouseData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          warehouseData.Data?.length !== 0
            ? warehouseDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setWarehouseModel(item);
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setWarehouseModel(item);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.Name,
                  <ValueOrNoDataLabel data={item.Description} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(5)]
        }
      />
    </div>
  );
}
